import {
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  Spinner,
  Stack,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import download from 'downloadjs';
import { FiDownload } from 'react-icons/fi';
import { Heading, Table } from '~/components';
import { Card } from '~/components/Card';
import logo from '~/assets/logo-service-gray.png';
import { downloadFile, getAnnouncementsById } from '~/services/Announcements';
import { formatInTimeZone } from '~/utils/formatToTimeZone';
import { AnnouncementsFile } from '~/types/announcements';
import { TextEditor } from '~/components/TextEditor/TextEditor';

export const AnnouncementDetail: React.VFC = () => {
  const history = useHistory();
  const toast = useToast();
  const [isFetching, setIsFetching] = useState(false);
  const { idComunicado } = useParams<{ idComunicado: string }>();
  const [titulo, setTitulo] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [arquivos, setArquivos] = useState<AnnouncementsFile[]>([]);
  const [dataCriacao, setDataCriacao] = useState('');
  const [dataPublicacao, setDataPublicacao] = useState('');
  const [motivoCancelamento, setMotivoCancelamento] = useState('');

  useEffect(() => {
    if (idComunicado) {
      setIsFetching(true);
      getAnnouncementsById(idComunicado)
        .then(response => {
          setTitulo(response.titulo);
          setMensagem(response.mensagem);
          response.arquivos?.length && setArquivos(response.formattedFiles);
          if (response.data_criacao) {
            const data = formatInTimeZone(response.data_criacao, 'dd/MM/yyyy');
            setDataCriacao(data);
          }
          if (response.data_publicacao) {
            const data = formatInTimeZone(
              response.data_publicacao,
              'dd/MM/yyyy',
            );
            setDataPublicacao(data);
          }
          if (response.motivo_cancelamento) {
            setMotivoCancelamento(response.motivo_cancelamento);
          }
        })
        .finally(() => setIsFetching(false));
    }
  }, [idComunicado]);

  const voltar = () => {
    history.goBack();
  };

  const downloadArquivo = (id: string) => {
    downloadFile(id)
      .then(r => {
        if (r?.data?.size > 0) {
          download(
            new Blob([r.data], { type: r.data.type }),
            r.headers['content-disposition'].match(/filename="(.+)"/)[1],
          );
        } else {
          toast({
            title: r?.data?.message,
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(() => {
        toast({
          title: 'Documento não encontrado',
          description: 'Entre em contato para mais informações',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      });
  };

  const content = () => {
    return (
      <Stack p={3} gridGap={3}>
        {motivoCancelamento && (
          <Text fontSize="16px">
            <b>Comunicado cancelado: </b>
            {motivoCancelamento}
          </Text>
        )}
        <Text fontSize="24px" fontWeight="bold">
          {titulo}
        </Text>
        <TextEditor value={mensagem} visualizacao />
        {arquivos?.length && (
          <>
            <Text fontWeight="bold" fontSize="14px">
              Arquivos anexados:
            </Text>
            <Table type="secondary">
              <Thead>
                <Tr>
                  <Th>NOME DO ARQUIVO</Th>
                  <Th textAlign="center">AÇÃO</Th>
                </Tr>
              </Thead>
              <Tbody>
                {arquivos?.length ? (
                  arquivos?.map(arquivo => (
                    <Tr key={arquivo.id}>
                      <Td>{arquivo.nome_arquivo}</Td>
                      <Td textAlign="center">
                        <Tooltip
                          hasArrow
                          label="Download"
                          bg="black"
                          shouldWrapChildren
                        >
                          <IconButton
                            onClick={() =>
                              downloadArquivo(arquivo.id.toString())
                            }
                            aria-label="Download"
                            icon={<Icon as={FiDownload} />}
                          />
                        </Tooltip>
                      </Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td textAlign="center" colSpan={6}>
                      Não há dados a serem apresentados
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </>
        )}
        <img src={logo} alt="" width="100px" />
      </Stack>
    );
  };

  return (
    <Box px="8" py="4">
      <HStack gridGap={3}>
        <Heading>Comunicados</Heading>
        {isFetching && <Spinner />}
      </HStack>
      <HStack
        py={8}
        justifyContent="space-between"
        alignItems="flex-end"
        w="70%"
      >
        <HStack gridGap={4}>
          <HStack>
            <Text fontWeight="bold">Data de criação: </Text>
            <Text>{dataCriacao}</Text>
          </HStack>
          <HStack>
            <Text fontWeight="bold">Data de publicação: </Text>
            <Text>{dataPublicacao}</Text>
          </HStack>
        </HStack>
      </HStack>
      <Card width="70%" content={content()} />
      <HStack justifyContent="flex-end" w="70%">
        <Button
          marginTop="40px"
          variant="outline"
          type="button"
          onClick={() => voltar()}
        >
          Voltar
        </Button>
      </HStack>
    </Box>
  );
};
